import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { navItems, type NavItem } from "../config/nav";
import { SITE_LOGO } from "../config/site";
import clsx from "clsx";
// import LangSwitcher from "./LangSwitcher";
import { ArrowUpRight, ChevronDown, ChevronRight, X } from "lucide-react";

export default function Header() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === "zh";
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  const isActive = (path: string) => pathname === path;

  // 判断当前菜单（或其后代）是否处于激活状态
  const isBranchActive = (item: NavItem): boolean => {
    if (isActive(item.path)) return true;
    return !!item.children?.some((child) => isBranchActive(child));
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => {
      if (prev) setExpandedPaths(new Set());
      return !prev;
    });
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setExpandedPaths(new Set());
  };

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    closeMobileMenu();
  }, [pathname]);

  const toggleExpand = (path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  // ============== 桌面端：二级及以下递归级联子菜单 ==============
  const DesktopSubItem = ({ item }: { item: NavItem }) => {
    const hasChildren = !!item.children?.length;
    const branchActive = isBranchActive(item);

    if (!hasChildren) {
      return (
        <Link
          to={item.path}
          className={clsx(
            "block px-4 py-2 text-sm text-left transition-colors whitespace-nowrap",
            branchActive
              ? "text-brand-700 bg-brand-50"
              : "text-ink-600 hover:bg-ink-50 hover:text-ink-900"
          )}
          onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "auto" })}
        >
          {t(item.translationKey)}
        </Link>
      );
    }

    return (
      <div className="relative group/sub">
        <div
          className={clsx(
            "flex items-center justify-between gap-2 px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-default",
            branchActive
              ? "text-brand-700 bg-brand-50"
              : "text-ink-600 hover:bg-ink-50 hover:text-ink-900"
          )}
        >
          <span>{t(item.translationKey)}</span>
          <ChevronRight className="size-3.5 opacity-60" />
        </div>
        {/* 三级菜单 */}
        <div className="absolute top-0 left-full pl-1 opacity-0 invisible group-hover/sub:opacity-100 group-hover/sub:visible transition-all duration-200 z-50">
          <div className="bg-white rounded-lg shadow-lg border border-ink-100 py-2 min-w-[180px]">
            {item.children!.map((grand) => (
              <DesktopSubItem key={grand.path} item={grand} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============== 移动端：递归可折叠菜单 ==============
  const MobileNavNode = ({
    item,
    depth,
  }: {
    item: NavItem;
    depth: number;
  }) => {
    const hasChildren = !!item.children?.length;
    const branchActive = isBranchActive(item);
    const linkActive = isActive(item.path);
    const expanded = expandedPaths.has(item.path);
    const linkTo = item.hash ? `${item.path}${item.hash}` : item.path;

    // 缩进随深度增加
    const paddingLeft = 30 + depth * 15;

    if (item.isCta) {
      return (
        <Link
          to={linkTo}
          className={clsx(
            "block leading-10 text-base text-white font-semibold border-b border-white/20",
            "bg-[#f89505] hover:bg-[#e08604]"
          )}
          style={{ paddingLeft, paddingRight: 12 }}
          onClick={closeMobileMenu}
        >
          {t(item.translationKey)}
        </Link>
      );
    }

    if (item.external) {
      return (
        <a
          href={item.externalUrl}
          target="_blank"
          rel="noreferrer"
          className={clsx(
            "text-white block border-b border-white/20",
            depth === 0 ? "leading-10 text-base" : "leading-10 text-sm border-white/50",
            branchActive ? "bg-white/15" : ""
          )}
          style={{ paddingLeft, paddingRight: 12 }}
          onClick={closeMobileMenu}
        >
          {t(item.translationKey)}
        </a>
      );
    }

    if (!hasChildren) {
      return (
        <Link
          to={item.path}
          className={clsx(
            "text-white block border-b",
            depth === 0
              ? "leading-10 text-base border-white/20"
              : "leading-10 text-sm border-white/10",
            linkActive ? "bg-white/15" : ""
          )}
          style={{ paddingLeft, paddingRight: 12 }}
          onClick={() => {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
            closeMobileMenu();
          }}
        >
          {t(item.translationKey)}
        </Link>
      );
    }

    return (
      <div>
        <button
          type="button"
          onClick={() => toggleExpand(item.path)}
          className={clsx(
            "w-full text-white flex items-center justify-between border-b text-left",
            depth === 0
              ? "leading-10 text-base border-white/20"
              : "leading-10 text-sm border-white/10",
            branchActive ? "bg-white/15" : ""
          )}
          style={{ paddingLeft, paddingRight: 12 }}
        >
          <span>{t(item.translationKey)}</span>
          <ChevronDown
            className={clsx(
              "size-4 transition-transform duration-200",
              expanded ? "rotate-180" : "rotate-0"
            )}
          />
        </button>
        {expanded && (
          <div className="bg-[#0a4a75]">
            {item.children!.map((child) => (
              <MobileNavNode key={child.path} item={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Desktop Header */}
      <div className="hidden md:block w-full h-20 fixed top-0 left-0 z-9999 bg-white">
        <div className="max-w-[1200px] mx-auto flex justify-between items-center px-5">
          <div className="mt-[9px]">
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "auto" })}
            >
              <img src={SITE_LOGO} alt="Logo" className="h-auto max-h-[62px]" />
            </Link>
          </div>
          <div className="flex justify-end max-xl:pr-20">
            <ul className="flex">
              {navItems.map((item) => {
                const hasChildren = !!item.children?.length;
                const isMenuActive = isBranchActive(item);
                const linkTo = item.hash ? `${item.path}${item.hash}` : item.path;

                // 立即咨询 CTA：强调按钮态
                if (item.isCta) {
                  return (
                    <li
                      key={`${item.path}${item.hash ?? ""}`}
                      className="flex items-center min-w-fit pl-4 mt-2.5"
                    >
                      <Link
                        to={linkTo}
                        className="inline-flex items-center justify-center rounded-md bg-[#f89505] hover:bg-[#e08604] active:scale-95 text-white font-semibold px-4 py-2 text-sm shadow-sm transition-all"
                        onClick={() => {
                          // 滚到顶部之外，由首页 #contact 自动定位
                        }}
                      >
                        {t(item.translationKey)}
                      </Link>
                    </li>
                  );
                }

                return (
                  <li
                    key={item.path}
                    className={clsx(
                      "group text-center leading-[60px] text-sm mt-2.5 relative",
                      isZh ? "min-w-fit px-4" : "min-w-fit px-4"
                    )}
                  >
                    {item.external ? (
                      <a
                        href={item.externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={clsx(
                          "relative h-[40px] leading-[40px] inline-flex items-center gap-1 transition-colors px-1.5",
                          isMenuActive
                            ? "text-brand-700"
                            : "text-ink-600 hover:bg-ink-100/70 hover:text-ink-900"
                        )}
                      >
                        <span className="relative">
                          {t(item.translationKey)}
                          <i
                            className={`absolute bottom-0 left-0 right-0 h-0.5 bg-brand-600 transition-all ${
                              isMenuActive ? "block" : "hidden group-hover:block"
                            }`}
                          ></i>
                        </span>
                        <ArrowUpRight className="size-3.5" />
                      </a>
                    ) : hasChildren ? (
                      <div className="relative group">
                        <button
                          className={clsx(
                            "relative h-[40px] leading-[40px] inline-flex items-center gap-1 transition-colors px-1.5 cursor-pointer",
                            isMenuActive
                              ? "text-brand-700"
                              : "text-ink-600 hover:bg-ink-100/70 hover:text-ink-900"
                          )}
                        >
                          <span className="relative">
                            {t(item.translationKey)}
                            <i
                              className={`absolute bottom-0 left-0 right-0 h-0.5 bg-brand-600 transition-all ${
                                isMenuActive ? "block" : "hidden group-hover:block"
                              }`}
                            ></i>
                          </span>
                          <ChevronDown className="size-3.5 transition-transform group-hover:rotate-180" />
                        </button>
                        {/* 二级下拉 */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                          <div className="bg-white rounded-lg shadow-lg border border-ink-100 py-2 min-w-[180px]">
                            {item.children!.map((child) => (
                              <DesktopSubItem key={child.path} item={child} />
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        className={clsx(
                          "relative h-[40px] leading-[40px] inline-block transition-colors px-1.5",
                          isMenuActive
                            ? "text-brand-700"
                            : "text-ink-600 hover:bg-ink-100/70 hover:text-ink-900"
                        )}
                        onClick={() => {
                          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
                        }}
                      >
                        <span className="relative pb-3">
                          {t(item.translationKey)}
                          <i
                            className={`absolute bottom-0 left-0 right-0 h-0.5 bg-brand-600 transition-all ${
                              isMenuActive ? "block" : "hidden group-hover:block"
                            }`}
                          ></i>
                        </span>
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 w-full h-[50px] z-9999 bg-white flex items-center justify-between px-4 shadow-[0_1px_4px_rgba(0,0,0,0.08)]">
        <Link
          to="/"
          className="flex items-center h-full"
          onClick={() => {
            closeMobileMenu();
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
          }}
        >
          <img src={SITE_LOGO} alt="Logo" className="h-8 w-auto" />
        </Link>
        <button
          type="button"
          onClick={toggleMobileMenu}
          className="relative h-10 w-10 flex flex-col items-center justify-center gap-[5px]"
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span
            className={clsx(
              "block h-0.5 w-5 bg-[#222] transition-transform duration-300",
              isMobileMenuOpen && "translate-y-[7px] rotate-45"
            )}
          />
          <span
            className={clsx(
              "block h-0.5 w-5 bg-[#222] transition-opacity duration-300",
              isMobileMenuOpen && "opacity-0"
            )}
          />
          <span
            className={clsx(
              "block h-0.5 w-5 bg-[#222] transition-transform duration-300",
              isMobileMenuOpen && "-translate-y-[7px] -rotate-45"
            )}
          />
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      <button
        type="button"
        aria-label="Close menu overlay"
        onClick={closeMobileMenu}
        className={clsx(
          "md:hidden fixed inset-0 z-9997 bg-black/40 transition-opacity duration-300",
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        )}
      />

      {/* Mobile Menu Panel */}
      <div
        className={clsx(
          "md:hidden fixed top-0 right-0 h-full w-[78%] max-w-[320px] bg-[#0f5f95] transition-transform duration-300 ease-in-out z-9998 shadow-xl",
          isMobileMenuOpen
            ? "translate-x-0 pointer-events-auto"
            : "translate-x-full pointer-events-none"
        )}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="h-[50px] flex items-center justify-end px-3 border-b border-white/15">
          <button
            type="button"
            onClick={closeMobileMenu}
            className="h-10 w-10 flex items-center justify-center text-white"
            aria-label="Close menu"
          >
            <X className="size-5" strokeWidth={2} />
          </button>
        </div>
        <ul className="overflow-y-auto max-h-[calc(100vh-50px)] pb-8">
          {navItems.map((item) => (
            <li key={`${item.path}${item.hash ?? ""}`}>
              <MobileNavNode item={item} depth={0} />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
